import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaUtensils, FaHotel, FaBus, FaBinoculars, FaClock, FaGlobe } from 'react-icons/fa';

const InternationalTours = () => {
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Helper function to format duration and prevent NaN
  const formatDuration = (duration) => {
    const defaultDays = 7;
    const days = parseInt(duration, 10);

    if (!isNaN(days) && days > 0) {
      const nights = days - 1;
      return `${days} Days / ${nights} Nights`;
    }
    
    const nights = defaultDays - 1;
    return `${defaultDays} Days / ${nights} Nights`;
  };

  useEffect(() => {
    const tourRef = ref(db, 'tours');
    onValue(tourRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const internationalTours = Object.entries(data)
          .map(([id, tour]) => ({ id, ...tour }))
          .filter(tour => tour.category?.toLowerCase() === 'international-tour');
        setTours(internationalTours);
      }
      setLoading(false);
    });
  }, []);

  const openTourModal = (tour) => {
    setSelectedTour(tour);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTour(null);
    document.body.style.overflow = 'auto';
  };

  if (loading) {
    return (
      <div className="international-loading">
        <div className="international-spinner"></div>
        <p>Loading International Tours...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        /* Loading Styles */
        .international-loading {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 60px 20px;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 1.1rem;
          color: #2193b0;
          min-height: 60vh;
        }

        .international-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(33, 147, 176, 0.2);
          border-top: 4px solid #2193b0;
          border-radius: 50%;
          animation: internationalSpin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes internationalSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Main Container */
        .international-tours-container {
          padding: 40px 20px;
          background: #f8f9fa;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Tours Grid - Fixed sizing for consistency */
        .international-tours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          padding: 0 10px;
          justify-items: center;
        }

        /* Tour Card - Fixed dimensions for consistency */
        .international-tour-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(33, 147, 176, 0.12);
          overflow: hidden;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          display: flex;
          flex-direction: column;
          border: 2px solid transparent;
          animation: internationalCardFadeIn 0.6s ease forwards;
          opacity: 0;
          transform: translateY(30px);
          /* Fixed consistent dimensions */
          width: 100%;
          max-width: 380px;
          min-height: 520px;
          height: auto;
        }

        .international-tour-card:nth-child(1) { animation-delay: 0.1s; }
        .international-tour-card:nth-child(2) { animation-delay: 0.2s; }
        .international-tour-card:nth-child(3) { animation-delay: 0.3s; }
        .international-tour-card:nth-child(4) { animation-delay: 0.4s; }
        .international-tour-card:nth-child(5) { animation-delay: 0.5s; }
        .international-tour-card:nth-child(6) { animation-delay: 0.6s; }

        @keyframes internationalCardFadeIn {
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        .international-tour-card:hover {
          transform: translateY(-12px) scale(1.02);
          box-shadow: 0 20px 40px rgba(33, 147, 176, 0.25);
          border-color: rgba(33, 147, 176, 0.3);
        }

        /* Fixed image dimensions */
        .international-tour-image {
          width: 100%;
          height: 240px;
          object-fit: cover;
          object-position: center;
          transition: transform 0.4s ease;
          display: block;
          background: #f0f4f8 url('https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center center/cover no-repeat;
          flex-shrink: 0;
        }

        .international-tour-card:hover .international-tour-image {
          transform: scale(1.08);
        }

        /* Card content with consistent spacing */
        .international-card-content {
          padding: 24px;
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: space-between;
          min-height: 280px;
        }

        .international-tour-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #0a0808;
          margin-bottom: 12px;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          min-height: 3.2em;
          flex-shrink: 0;
        }

        .international-tour-duration {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          color: #050505;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          background: rgba(102, 126, 234, 0.1);
          padding: 8px 12px;
          border-radius: 12px;
          width: fit-content;
          flex-shrink: 0;
        }

        .international-duration-icon {
          margin-right: 8px;
          color: #0a0808;
          font-size: 0.85rem;
        }

        /* Features grid with consistent sizing */
        .international-tour-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-bottom: 20px;
          width: 100%;
          flex: 1;
        }

        .international-feature-tag {
          background: linear-gradient(135deg, #2193b0, #6dd5ed);
          color: white;
          padding: 10px 12px;
          border-radius: 20px;
          font-family: 'Poppins', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: nowrap;
          box-shadow: 0 3px 10px rgba(33, 147, 176, 0.3);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
          min-height: 38px;
          max-height: 38px;
        }

        .international-feature-tag::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .international-feature-tag:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(33, 147, 176, 0.4);
        }

        .international-feature-tag:hover::before {
          left: 100%;
        }

        .international-feature-tag .icon {
          margin-right: 6px;
          font-size: 0.7rem;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
        }

        .international-view-button {
          align-self: center;
          background: linear-gradient(135deg, #2193b0, #6dd5ed);
          color: white;
          padding: 12px 24px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(33, 147, 176, 0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          width: fit-content;
          min-width: 140px;
          flex-shrink: 0;
        }

        .international-view-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          transition: all 0.6s ease;
          transform: translate(-50%, -50%);
        }

        .international-view-button:hover::before {
          width: 200px;
          height: 200px;
        }

        .international-view-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 32px rgba(33, 147, 176, 0.4);
        }

        /* No tours section */
        .international-no-tours {
          text-align: center;
          padding: 50px 30px;
          max-width: 600px;
          margin: 40px auto;
          background: white;
          border-radius: 20px;
          box-shadow: 0 16px 32px rgba(33, 147, 176, 0.4);
          font-family: 'Poppins', sans-serif;
        }

        .international-no-tours-icon {
          font-size: 3rem;
          color: #dfe2ecff;
          margin-bottom: 20px;
        }

        .international-no-tours-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.8rem;
          color: #050505;
          margin-bottom: 16px;
          font-weight: 700;
        }

        .international-no-tours-text {
          font-family: 'Poppins', sans-serif;
          font-size: 1rem;
          color: #2193b0;
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .international-contact-link {
          display: inline-block;
          background: linear-gradient(135deg, #2193b0, #6dd5ed);
          color: white;
          padding: 12px 24px;
          border-radius: 30px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .international-contact-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(33, 147, 176, 0.3);
        }

        /* Modal Styles */
        .international-modal-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
          backdrop-filter: blur(8px);
          animation: internationalOverlayFadeIn 0.3s ease;
        }

        @keyframes internationalOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .international-modal {
          background: white;
          border-radius: 20px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px rgba(33, 147, 176, 0.3);
          animation: internationalModalSlideIn 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          display: flex;
          flex-direction: column;
        }

        @keyframes internationalModalSlideIn {
          from { 
            opacity: 0; 
            transform: scale(0.9) translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }

        .international-modal-header {
          position: relative;
          overflow: hidden;
        }

        .international-modal-image {
          width: 100%;
          height: 300px;
          object-fit: cover;
          object-position: center;
          border-radius: 20px 20px 0 0;
          display: block;
          background: #f0f4f8 url('https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center center/cover no-repeat;
        }

        .international-close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 1.1rem;
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .international-close-button:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: rotate(90deg) scale(1.1);
        }

        .international-modal-body {
          padding: 32px;
          display: flex;
          flex-direction: column;
          font-family: 'Poppins', sans-serif;
        }

        .international-modal-title {
          font-family: 'Poppins', sans-serif;
          font-size: 2rem;
          color: #050505;
          font-weight: 700;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .international-modal-subtitle {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          color: #050505;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          margin-bottom: 24px;
          font-size: 1rem;
        }

        .international-modal-subtitle span {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .international-modal-description {
          color: #050505;
          font-family: 'Poppins', sans-serif;
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 32px;
        }

        .international-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .international-feature-card {
          background: linear-gradient(135deg, #f8f9ff, #e8f0fe);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          border: 1px solid rgba(102, 126, 234, 0.1);
          transition: transform 0.3s ease;
        }

        .international-feature-card:hover {
          transform: translateY(-4px);
        }

        .international-feature-icon {
          color: #2193b0;
          font-size: 1.5rem;
          min-width: 32px;
          display: flex;
          justify-content: center;
          margin-top: 4px;
        }

        .international-feature-content h4 {
          margin-bottom: 8px;
          color: #0a0808;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 1rem;
        }

        .international-feature-content p {
          font-family: 'Poppins', sans-serif;
          font-size: 0.9rem;
          color: #555;
          line-height: 1.5;
        }

        .international-modal::-webkit-scrollbar {
          width: 8px;
        }

        .international-modal::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .international-modal::-webkit-scrollbar-thumb {
          background: #2193b0;
          border-radius: 4px;
        }

        .international-modal::-webkit-scrollbar-thumb:hover {
           background: #1a7a8a;
        }

        /* Responsive Breakpoints */
        @media (min-width: 1400px) {
          .international-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
            gap: 28px;
            max-width: 1600px;
          }
          .international-tour-card {
            max-width: 400px;
            min-height: 550px;
          }
          .international-tour-image { height: 260px; }
          .international-card-content { min-height: 290px; }
        }

        @media (max-width: 1200px) {
          .international-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 22px;
            max-width: 1200px;
          }
          .international-tour-card {
            max-width: 360px;
            min-height: 500px;
          }
          .international-tour-image { height: 220px; }
          .international-card-content { min-height: 280px; }
          .international-modal-image { height: 280px; }
          .international-modal-title { font-size: 1.8rem; }
        }

        @media (max-width: 992px) {
          .international-tours-container { padding: 30px 15px; }
          .international-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 20px;
          }
          .international-tour-card {
            max-width: 340px;
            min-height: 480px;
          }
          .international-tour-image { height: 200px; }
          .international-card-content {
            padding: 20px;
            min-height: 280px;
          }
          .international-modal { max-width: 700px; }
          .international-modal-image { height: 250px; }
          .international-modal-body { padding: 24px; }
          .international-modal-title { font-size: 1.6rem; }
          .international-features-grid {
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .international-tours-container { padding: 20px 10px; }
          .international-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 16px;
            padding: 0 5px;
          }
          .international-tour-card {
            max-width: 100%;
            min-height: 460px;
          }
          .international-tour-image { height: 180px; }
          .international-card-content {
            padding: 18px;
            min-height: 280px;
          }
          .international-tour-title { font-size: 1.2rem; }
          .international-modal { max-width: 95vw; margin: 10px; }
          .international-modal-image { height: 220px; }
          .international-modal-body { padding: 20px; }
          .international-modal-title { font-size: 1.4rem; }
          .international-modal-subtitle {
            flex-direction: column;
            gap: 12px;
          }
          .international-features-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .international-feature-card { padding: 16px; }
          .international-no-tours {
            padding: 40px 20px;
            margin: 20px auto;
          }
          .international-no-tours-title { font-size: 1.5rem; }
        }

        @media (max-width: 576px) {
          .international-tours-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .international-tour-card {
            min-height: 420px;
            max-width: 100%;
          }
          .international-tour-image { height: 160px; }
          .international-card-content {
            padding: 16px;
            min-height: 260px;
          }
          .international-tour-title {
            font-size: 1.1rem;
            margin-bottom: 10px;
            min-height: 2.8em;
          }
          .international-tour-duration {
            font-size: 0.85rem;
            padding: 6px 10px;
          }
          .international-tour-features { gap: 6px; }
          .international-feature-tag {
            font-size: 0.7rem;
            padding: 8px 10px;
            min-height: 36px;
            max-height: 36px;
          }
          .international-feature-tag .icon {
            font-size: 0.65rem;
            margin-right: 4px;
          }
          .international-view-button {
            padding: 10px 20px;
            font-size: 0.9rem;
            min-width: 120px;
          }
          .international-modal-image { height: 200px; }
          .international-modal-body { padding: 16px; }
          .international-modal-title { font-size: 1.3rem; }
          .international-modal-subtitle { font-size: 0.9rem; }
          .international-modal-description { font-size: 0.9rem; }
          .international-feature-card { padding: 14px; gap: 12px; }
          .international-feature-icon {
            font-size: 1.3rem;
            min-width: 28px;
          }
          .international-close-button {
            width: 36px;
            height: 36px;
            font-size: 1rem;
          }
          .international-no-tours { padding: 30px 15px; }
          .international-no-tours-icon { font-size: 2.5rem; }
          .international-no-tours-title { font-size: 1.3rem; }
          .international-no-tours-text { font-size: 0.9rem; }
        }

        @media (max-width: 480px) {
          .international-tours-container { padding: 15px 5px; }
          .international-tours-grid { padding: 0; gap: 12px; }
          .international-tour-card {
            min-height: 400px;
          }
          .international-tour-image { height: 150px; }
          .international-card-content {
            padding: 14px;
            min-height: 250px;
          }
          .international-tour-title {
            font-size: 1rem;
            line-height: 1.2;
            min-height: 2.4em;
          }
          .international-tour-duration {
            font-size: 0.8rem;
            padding: 5px 8px;
          }
          .international-tour-features { gap: 5px; }
          .international-feature-tag {
            font-size: 0.68rem;
            padding: 6px 8px;
            border-radius: 16px;
            min-height: 32px;
            max-height: 32px;
          }
          .international-feature-tag .icon {
            font-size: 0.6rem;
            margin-right: 3px;
          }
          .international-view-button {
            padding: 8px 16px;
            font-size: 0.85rem;
            min-width: 100px;
          }
          .international-modal {
            margin: 5px;
            max-width: calc(100vw - 10px);
          }
          .international-modal-image { height: 180px; }
          .international-modal-body { padding: 14px; }
          .international-modal-title { font-size: 1.2rem; }
          .international-modal-subtitle { font-size: 0.85rem; }
          .international-modal-description { font-size: 0.85rem; }
          .international-feature-card { padding: 12px; }
          .international-feature-content h4 { font-size: 0.9rem; }
          .international-feature-content p { font-size: 0.8rem; }
          .international-close-button {
            width: 32px;
            height: 32px;
            font-size: 0.9rem;
            top: 12px;
            right: 12px;
          }
          .international-no-tours {
            padding: 25px 10px;
            margin: 10px auto;
          }
          .international-no-tours-title { font-size: 1.2rem; }
          .international-no-tours-text { font-size: 0.85rem; }
          .international-contact-link {
            font-size: 0.85rem;
            padding: 8px 16px;
          }
        }

        @media (max-width: 360px) {
          .international-tours-grid { gap: 10px; }
          .international-tour-card {
            min-height: 380px;
          }
          .international-tour-image { height: 140px; }
          .international-card-content {
            padding: 12px;
            min-height: 240px;
          }
          .international-tour-title {
            font-size: 0.95rem;
            min-height: 2.2em;
          }
          .international-tour-features { gap: 4px; }
          .international-feature-tag {
            font-size: 0.65rem;
            padding: 5px 7px;
            border-radius: 14px;
            min-height: 30px;
            max-height: 30px;
          }
          .international-feature-tag .icon {
            font-size: 0.58rem;
            margin-right: 2px;
          }
          .international-modal-image { height: 160px; }
          .international-modal-title { font-size: 1.1rem; }
          .international-feature-card {
            padding: 10px;
            gap: 10px;
          }
          .international-feature-icon {
            font-size: 1.2rem;
            min-width: 24px;
          }
          .international-no-tours { padding: 20px 8px; }
          .international-no-tours-icon { font-size: 2rem; }
          .international-no-tours-title { font-size: 1.1rem; }
          .international-contact-link {
            font-size: 0.8rem;
            padding: 6px 12px;
          }
        }
      `}</style>

      <div className="international-tours-container">
        {loading ? null : tours.length === 0 ? (
          <div className="international-no-tours">
            <div className="international-no-tours-icon">
              <FaGlobe />
            </div>
            <h3 className="international-no-tours-title">No International Tours Available</h3>
            <p className="international-no-tours-text">
              We're currently updating our international tour packages. Check back soon for exciting new destinations around the world!
            </p>
            <a href="/contact-us" className="international-contact-link">
              Contact Us for Updates
            </a>
          </div>
        ) : (
          <div className="international-tours-grid">
            {tours.map((tour, index) => (
              <div
                className="international-tour-card"
                key={tour.id}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openTourModal(tour)}
              >
                <img
                  src={tour.image || 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'}
                  alt={tour.title || 'International Tour'}
                  className="international-tour-image"
                  loading="lazy"
                />
                <div className="international-card-content">
                  <h3 className="international-tour-title">
                    {tour.title || 'International Tour Package'}
                  </h3>
                  <div className="international-tour-duration">
                    <FaClock className="international-duration-icon" />
                    {formatDuration(tour.duration)}
                  </div>
                  <div className="international-tour-features">
                    <span className="international-feature-tag">
                      <FaUtensils className="icon" /> Meals
                    </span>
                    <span className="international-feature-tag">
                      <FaHotel className="icon" /> Stay
                    </span>
                    <span className="international-feature-tag">
                      <FaBus className="icon" /> Transport
                    </span>
                    <span className="international-feature-tag">
                      <FaBinoculars className="icon" /> Sightseeing
                    </span>
                  </div>
                  <button className="international-view-button">
                    <span style={{ position: 'relative', zIndex: 1 }}>View Details</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {isModalOpen && selectedTour && (
          <div className="international-modal-overlay" onClick={closeModal}>
            <div className="international-modal" onClick={e => e.stopPropagation()}>
              <div className="international-modal-header">
                <img
                  src={selectedTour.image || 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'}
                  alt={selectedTour.title || 'International Tour'}
                  className="international-modal-image"
                  loading="lazy"
                />
                <button onClick={closeModal} className="international-close-button">
                  <FaTimes />
                </button>
              </div>
              <div className="international-modal-body">
                <h2 className="international-modal-title">{selectedTour.title}</h2>
                <div className="international-modal-subtitle">
                  <span>
                    <FaMapMarkerAlt /> {selectedTour.destination || 'Multiple Countries'}
                  </span>
                  <span>
                    <FaCalendarAlt />
                    {formatDuration(selectedTour.duration)}
                  </span>
                </div>
                <p className="international-modal-description">
                  {selectedTour.description || 'This incredible international tour package takes you through some of the most beautiful destinations in the world. Experience diverse cultures, breathtaking landscapes, and unforgettable memories with our expertly planned itinerary.'}
                </p>

                <div className="international-features-grid">
                  <div className="international-feature-card">
                    <div className="international-feature-icon">
                      <FaUtensils />
                    </div>
                    <div className="international-feature-content">
                      <h4>Meals</h4>
                      <p>{selectedTour.meals || 'Daily breakfast and dinner included with local cuisine'}</p>
                    </div>
                  </div>
                  <div className="international-feature-card">
                    <div className="international-feature-icon">
                      <FaHotel />
                    </div>
                    <div className="international-feature-content">
                      <h4>Accommodation</h4>
                      <p>{selectedTour.accommodation || '4-star hotels with premium amenities'}</p>
                    </div>
                  </div>
                  <div className="international-feature-card">
                    <div className="international-feature-icon">
                      <FaBus />
                    </div>
                    <div className="international-feature-content">
                      <h4>Transportation</h4>
                      <p>{selectedTour.transfers || 'All transfers and transportation included'}</p>
                    </div>
                  </div>
                  <div className="international-feature-card">
                    <div className="international-feature-icon">
                      <FaBinoculars />
                    </div>
                    <div className="international-feature-content">
                      <h4>Sightseeing</h4>
                      <p>{selectedTour.sightseeing || 'Guided tours and sightseeing included'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InternationalTours;
